import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { Card } from 'primereact/card';
import { Button } from 'primereact/button';
import { InputText } from 'primereact/inputtext';
import { InputTextarea } from 'primereact/inputtextarea';
import { Dropdown } from 'primereact/dropdown';
import { Toast } from 'primereact/toast';
import { AutoComplete, AutoCompleteCompleteEvent, AutoCompleteChangeEvent } from 'primereact/autocomplete'; // Ensure AutoComplete is imported
import { createTicket, updateTicket, getTicket, getUsers, uploadTicketAttachment, getPurchaseOrders, getPurchaseOrderById } from '../api';
import { Ticket, TicketPriority, CreateTicketDto, UpdateTicketDto } from '../types/ticket';
import { User } from '../types/user';
import { PurchaseOrder } from '../types/purchaseOrder'; // Import PurchaseOrder type

const TicketForm: React.FC = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const toast = React.useRef<Toast>(null);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [users, setUsers] = useState<User[]>([]);
  const [searching, setSearching] = useState(false);
  const [filteredPOs, setFilteredPOs] = useState<any[]>([]);
  const [selectedPO, setSelectedPO] = useState<{ label: string; value: number } | null>(null); // State for AutoComplete value object
  const [searchParams] = useSearchParams();
  const poId = searchParams.get('poId');
  const fileInputRef = React.useRef<HTMLInputElement>(null);
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  
  const [formData, setFormData] = useState<CreateTicketDto & UpdateTicketDto>({
    title: '',
    description: '',
    priority: TicketPriority.MEDIUM,
    assignedToId: undefined,
    purchaseOrderId: poId ? Number(poId) : undefined
  });

  // Effect to load initial PO details if poId is present in URL or form data
  useEffect(() => {
    const initialPoId = formData.purchaseOrderId;
    if (initialPoId) {
      loadInitialPO(initialPoId);
    } else {
      setSelectedPO(null); // Clear selection if no initial ID
    }
  }, [formData.purchaseOrderId]); // Rerun when purchaseOrderId changes

  useEffect(() => {
    loadUsers();
    if (id) {
      loadTicket();
    } else if (poId) {
      // If creating a new ticket and poId is in URL, load its details
      loadInitialPO(Number(poId));
    }
  }, [id, poId]); // Add poId dependency

  const loadUsers = async () => {
    try {
      const data = await getUsers();
      setUsers(data);
    } catch (error) {
      console.error('Error loading users:', error);
      toast.current?.show({
        severity: 'error',
        summary: 'Error',
        detail: 'Failed to load users',
        life: 3000
      });
    }
  };

  const loadTicket = async () => {
    try {
      const data = await getTicket(Number(id));
      setFormData({
        title: data.title,
        description: data.description,
        priority: data.priority,
        assignedToId: data.assignedTo?.id,
        purchaseOrderId: data.purchaseOrderId // Load existing purchaseOrderId
      });
      // Initial PO loading is handled by the separate useEffect
    } catch (error) {
      console.error('Error loading ticket:', error);
      toast.current?.show({
        severity: 'error',
        summary: 'Error',
        detail: 'Failed to load ticket',
        life: 3000
      });
    }
  };

  // Function to load PO details for initial display in AutoComplete
  const loadInitialPO = async (poIdToLoad: number) => {
    try {
      const poData = await getPurchaseOrderById(poIdToLoad);
      setSelectedPO({ label: `#${poData.poNumber}`, value: poData.id });
    } catch (error) {
      console.error(`Error loading initial PO details for ID ${poIdToLoad}:`, error);
      // Don't show toast, just clear selection if PO not found or error occurs
      setSelectedPO(null);
      // Optionally clear the ID from formData if the PO doesn't exist?
      // setFormData(prev => ({ ...prev, purchaseOrderId: undefined }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      let ticketId = id ? Number(id) : undefined;
      
      if (ticketId) {
        await updateTicket(ticketId, formData);
      } else {
        const newTicket = await createTicket(formData);
        ticketId = newTicket.id;
      }

      toast.current?.show({
        severity: 'success',
        summary: 'Success',
        detail: `Ticket ${id ? 'updated' : 'created'} successfully`,
        life: 3000
      });

      // If there are files to upload, do it after ticket creation/update
      if (selectedFiles.length > 0) {
        setUploading(true);
        try {
          for (const file of selectedFiles) {
            await uploadTicketAttachment(ticketId, file);
          }
          toast.current?.show({
            severity: 'success',
            summary: 'Success',
            detail: 'Files uploaded successfully',
            life: 3000
          });
        } catch (error) {
          console.error('Error uploading files:', error);
          toast.current?.show({
            severity: 'error',
            summary: 'Error',
            detail: 'Failed to upload files',
            life: 3000
          });
        } finally {
          setUploading(false);
        }
      }

      navigate('/tickets');
    } catch (error) {
      console.error('Error saving ticket:', error);
      toast.current?.show({
        severity: 'error',
        summary: 'Error',
        detail: `Failed to ${id ? 'update' : 'create'} ticket`,
        life: 3000
      });
    } finally {
      setLoading(false);
    }
  };

  const priorityOptions = [
    { label: 'Low', value: TicketPriority.LOW },
    { label: 'Medium', value: TicketPriority.MEDIUM },
    { label: 'High', value: TicketPriority.HIGH }
  ];

  return (
    <div className="card">
      <Toast ref={toast} />
      
      <div className="flex justify-content-between mb-3">
        <div>
          <h1>{id ? 'Edit' : 'Create'} Ticket</h1>
          {poId && (
            <div className="text-sm text-500">
              Linked to Purchase Order #{poId}
            </div>
          )}
        </div>
        <Button
          label="Back"
          icon="pi pi-arrow-left"
          className="p-button-secondary"
          onClick={() => navigate(poId ? `/purchase-orders/${poId}` : '/tickets')}
        />
      </div>

      <Card>
        <form onSubmit={handleSubmit} className="flex flex-column gap-3">
          <div className="flex flex-column gap-2">
            <label htmlFor="title">Title</label>
            <InputText
              id="title"
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              required
            />
          </div>

          <div className="flex flex-column gap-2">
            <label htmlFor="description">Description</label>
            <InputTextarea
              id="description"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              rows={5}
              autoResize
              required
            />
          </div>

          <div className="flex flex-column gap-2">
            <label htmlFor="priority">Priority</label>
            <Dropdown
              id="priority"
              value={formData.priority}
              options={priorityOptions}
              onChange={(e) => setFormData({ ...formData, priority: e.value })}
              placeholder="Select Priority"
            />
          </div>

          <div className="flex flex-column gap-2">
            <label htmlFor="purchaseOrder">Link Purchase Order</label>
            <div className="flex align-items-center gap-2">
              <AutoComplete
                value={selectedPO} // Bind value to the selectedPO state object
                suggestions={filteredPOs}
                completeMethod={async (e: AutoCompleteCompleteEvent) => {
                  try {
                    setSearching(true);
                    const [pos] = await getPurchaseOrders({
                      page: 1,
                      limit: 10,
                      search: e.query,
                      sortBy: 'createdAt',
                      sortOrder: 'desc'
                    });
                    setFilteredPOs(pos.map(po => ({
                      label: `#${po.poNumber}`,
                      value: po.id
                    })));
                  } catch (error) {
                    console.error('Error searching purchase orders:', error);
                    toast.current?.show({
                      severity: 'error',
                      summary: 'Error',
                      detail: 'Failed to search purchase orders',
                      life: 3000
                    });
                  } finally {
                    setSearching(false);
                  }
                }}
                field="label"
                placeholder="Search Purchase Orders by PO Number"
                className="w-full"
                onChange={(e: AutoCompleteChangeEvent) => {
                  setSelectedPO(e.value); // Update the selected PO object state
                  setFormData({ ...formData, purchaseOrderId: e.value?.value }); // Update the ID in formData
                }}
                disabled={searching} // Disable while searching instead
              />
              {selectedPO && ( // Show unlink button if a PO object is selected
                <Button
                  icon="pi pi-times"
                  rounded
                  text
                  severity="danger"
                  onClick={() => {
                    setSelectedPO(null); // Clear the selected PO object state
                    setFormData({ ...formData, purchaseOrderId: undefined }); // Clear the ID in formData
                  }}
                  tooltip="Unlink Purchase Order"
                />
              )}
            </div>
          </div>

          <div className="flex flex-column gap-2">
            <label htmlFor="assignedTo">Assign To</label>
            <Dropdown
              id="assignedTo"
              value={formData.assignedToId}
              options={users.map(user => ({ label: user.username, value: user.id }))}
              onChange={(e) => setFormData({ ...formData, assignedToId: e.value })}
              placeholder="Select User"
            />
          </div>

          <div className="flex flex-column gap-2">
            <label>Attachments</label>
            <div className="flex align-items-center gap-2">
              <input
                type="file"
                multiple
                ref={fileInputRef}
                style={{ display: 'none' }}
                onChange={(e) => {
                  const files = Array.from(e.target.files || []);
                  setSelectedFiles(prev => [...prev, ...files]);
                }}
              />
              <Button
                type="button"
                label="Select Files"
                icon="pi pi-paperclip"
                onClick={() => fileInputRef.current?.click()}
              />
              {selectedFiles.length > 0 && (
                <span className="text-500">
                  {selectedFiles.length} file(s) selected
                </span>
              )}
            </div>
            {selectedFiles.length > 0 && (
              <div className="flex flex-column gap-2">
                {selectedFiles.map((file, index) => (
                  <div key={index} className="flex align-items-center justify-content-between surface-50 p-2 border-round">
                    <span className="flex align-items-center gap-2">
                      <i className="pi pi-file" />
                      {file.name}
                    </span>
                    <Button
                      icon="pi pi-times"
                      rounded
                      text
                      severity="danger"
                      onClick={() => {
                        setSelectedFiles(prev => prev.filter((_, i) => i !== index));
                      }}
                    />
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="flex gap-2 justify-content-end">
            <Button
              label="Cancel"
              icon="pi pi-times"
              className="p-button-secondary"
              onClick={() => navigate('/tickets')}
              type="button"
            />
            <Button
              label={id ? 'Update' : 'Create'}
              icon="pi pi-check"
              loading={loading || uploading}
              type="submit"
            />
          </div>
        </form>
      </Card>
    </div>
  );
};

export default TicketForm;
